import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MODULES } from "../data/modules";
import { useI18n } from "../i18n/I18nContext";

export default function Test() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const [moduleId, setModuleId] = useState(null);
  const [moduleName, setModuleName] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/connexion");
  }, [user, navigate]);

  useEffect(() => {
    const p = searchParams.get("module");
    if (p != null && !isNaN(Number(p))) {
      const id = Number(p);
      setModuleId(id);
      setModuleName(MODULES[id]?.name || null);
      return;
    }
    api.get("/subscriptions/me")
      .then(({ data }) => {
        if (data.subscription) {
          setModuleId(data.subscription.module.id);
          setModuleName(data.subscription.module.name);
        } else {
          setModuleId(-1);
        }
      })
      .catch(() => setModuleId(-1));
  }, [searchParams]);

  useEffect(() => {
    if (moduleId === null) return;
    const url = moduleId >= 0 ? `/tests/questions?module_id=${moduleId}` : "/tests/questions";
    api.get(url).then(({ data }) => setQuestions(data)).catch(() => {});
  }, [moduleId]);

  const answer = async (qid, v) => {
    const next = { ...answers, [qid]: v };
    setAnswers(next);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      try {
        const { data } = await api.post("/tests/run", { answers: next, module_id: moduleId >= 0 ? moduleId : null });
        setResult(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  if (!user) return null;
  const q = questions[step];
  const scale = t("test.scale");
  const dims = t("test.dims");
  const questionText = (question) => {
    const translated = t(`test.q.${question.id}`);
    return typeof translated === "string" && translated !== `test.q.${question.id}` ? translated : question.text;
  };
  const radarData = result
    ? Object.entries(result.scores).map(([k, v]) => ({ dim: dims[k] || k, score: v }))
    : [];

  return (
    <div data-testid="test-page" className="bg-paper min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-[72px]">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
          <div className="h-1 bg-linen rounded-full overflow-hidden" data-testid="test-progress">
            <motion.div className="h-full bg-sage" animate={{ width: result ? "100%" : `${(step / Math.max(questions.length, 1)) * 100}%` }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} />
          </div>

          <AnimatePresence mode="wait">
            {result ? (
              <motion.div key="result" data-testid="test-result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="pt-14">
                <p className="text-xs uppercase tracking-[0.28em] text-sage">{t("test.resultOver")}</p>
                <h1 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight text-ink">
                  {t("test.dominant")} <em className="italic text-sagedark">{dims[result.dominant]}</em>
                </h1>
                <div className="mt-10 rounded-3xl bg-white p-6 md:p-10">
                  <ResponsiveContainer width="100%" height={360}>
                    <RadarChart data={radarData} outerRadius="75%">
                      <PolarGrid stroke="#E5E1D8" />
                      <PolarAngleAxis dataKey="dim" tick={{ fill: "#1A1D1A", fontSize: 14, fontFamily: "Cormorant Garamond" }} />
                      <Radar dataKey="score" stroke="#7A8B73" fill="#7A8B73" fillOpacity={0.35} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(result.scores).map(([k, v]) => (
                      <div key={k} data-testid={`test-score-${k}`} className="text-center">
                        <p className="font-serif text-3xl text-ink">{v}</p>
                        <p className="text-xs uppercase tracking-[0.18em] text-smoke mt-1">{dims[k] || k}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link data-testid="test-result-espace-cta" to="/espace-client" className="rounded-full bg-ink text-paper px-8 py-4 text-sm hover:bg-sagedark transition-colors duration-300">
                    {t("test.espaceCta")}
                  </Link>
                  <button data-testid="test-restart-button" onClick={() => { setStep(0); setAnswers({}); setResult(null); }} className="rounded-full border border-ink/25 px-8 py-4 text-sm hover:border-ink transition-colors duration-300">
                    {t("test.restart")}
                  </button>
                </div>
              </motion.div>
            ) : q ? (
              <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="pt-14">
                <p className="text-xs uppercase tracking-[0.28em] text-sage">{t("test.question")} {step + 1} / {questions.length}</p>
                {moduleName && <p className="mt-3 text-xs uppercase tracking-[0.2em] text-smoke">{t("test.adapted").replace("{m}", moduleName)}</p>}
                <h1 className="mt-6 font-serif text-3xl md:text-5xl tracking-tight text-ink leading-tight">{questionText(q)}</h1>
                <div className="mt-12 grid gap-3">
                  {scale.map((label, i) => (
                    <button
                      key={i + 1}
                      data-testid={`test-answer-${q.id}-${i + 1}`}
                      onClick={() => answer(q.id, i + 1)}
                      className="group flex items-center justify-between bg-white rounded-2xl border border-linen px-6 md:px-8 py-4 hover:border-sage hover:shadow-[0_16px_40px_-20px_rgba(26,29,26,0.25)] transition-[border-color,box-shadow] duration-300"
                    >
                      <span className="text-base text-ink">{label}</span>
                      <span className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((d) => (
                          <span key={d} className={`w-2.5 h-2.5 rounded-full ${d <= i + 1 ? "bg-sage" : "bg-linen"}`} />
                        ))}
                      </span>
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button data-testid="test-back-button" onClick={() => setStep(step - 1)} className="mt-8 text-sm text-smoke hover:text-ink transition-colors duration-300">
                    {t("test.back")}
                  </button>
                )}
              </motion.div>
            ) : (
              <p className="pt-32 text-center font-serif italic text-2xl text-smoke">{t("test.prep")}</p>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
}
